import { theme } from 'antd';
import { groupPermissionsByTeam } from 'library/helpers/accessControl';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectPermissionManageableDevices } from 'reduxStore/devices/selectors';
import { selectPermissionsByTeamID } from 'reduxStore/permissions/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { Loader } from 'zComponents/atoms';
import { DevicesIcon } from 'assets/images/svg';
import { Empty, Flex, Popover, Tag, Typography } from 'zui/Antd';

const { Text } = Typography;

const PermissionsPanelContent = ({ teamID }) => {
	// ✅ Hooks for styling
	const {
		token: { fontWeightStrong, paddingSM, paddingXXS, colorTextDescription }
	} = theme.useToken();

	// ✅ Redux State
	const permissions = useSelector((state) => selectPermissionsByTeamID(state, teamID));
	const devices = useSelector(selectPermissionManageableDevices);
	const deviceGroups = useSelector(selectAssetGroups);
	const allTeams = useSelector(selectTeams);
	const { loadingAddPermission, loadingDeletePermission } = useSelector((state) => state.Permissions);

	// ✅ Process items specifically for Team Audit
	const displayItems = useMemo(() => {
		return groupPermissionsByTeam({
			permissions,
			devices,
			deviceGroups,
			teams: allTeams,
			currentTeamID: teamID
		});
	}, [permissions, devices, deviceGroups, allTeams, teamID]);

	if (loadingAddPermission || loadingDeletePermission) {
		return (
			<Flex vertical align='center' gap={paddingSM} style={{ width: '100%', padding: paddingSM }}>
				<Loader />
				<Text type='secondary' style={{ fontSize: 12, textAlign: 'center' }}>
					Propagating permissions to devices — this may take a moment.
					<br />
					We'll notify you when it's done.
				</Text>
			</Flex>
		);
	}

	if (displayItems.length === 0) {
		return (
			<Flex justify='center' align='start' style={{ width: '100%' }}>
				<Empty description='No access permissions' image={Empty.PRESENTED_IMAGE_SIMPLE} />
			</Flex>
		);
	}

	return (
		<Flex wrap gap={paddingSM} style={{ width: '100%' }}>
			{displayItems.map((item) => (
				<Popover
					key={item.permissionID}
					trigger='hover'
					placement='bottom'
					title={item.tooltip}
					content={
						item.deviceNames?.length > 0 ? (
							<Flex vertical gap={4} style={{ maxWidth: 250, maxHeight: 363, overflowY: 'auto' }}>
								{item.deviceNames.map((name, i) => (
									<Flex key={i} align='center' gap={6}>
										<DevicesIcon color={colorTextDescription} />
										<Text style={{ fontSize: 12 }}>{name}</Text>
									</Flex>
								))}
							</Flex>
						) : null
					}
				>
					<Tag
						color={item.badgeColor}
						style={{
							fontWeight: fontWeightStrong,
							borderColor: 'currentColor',
							marginInlineEnd: 0,
							cursor: 'pointer'
						}}
					>
						<Flex align='center' gap={paddingXXS}>
							<item.Icon color='currentColor' />
							{item.label}
						</Flex>
					</Tag>
				</Popover>
			))}
		</Flex>
	);
};

export default PermissionsPanelContent;
